<template>
    <div class="slide-in">
        <div v-if="loading" class="text-center py-5 text-muted">
            <div class="spinner-border text-secondary mb-3" role="status"></div>
            <div>Carregando configurações do prompt...</div>
        </div>

        <div v-else>
            <!-- Cabeçalho Padronizado PMG -->
            <div class="d-flex justify-content-between align-items-center mb-4 pb-3 border-bottom">
                <div>
                    <div class="d-flex align-items-center gap-2 mb-1">
                        <h5 class="fw-bold text-dark mb-0">{{ form.title || 'Prompt Mestre' }}</h5>
                        <span class="badge bg-secondary-subtle text-secondary fw-semibold fs-8">
                            Versão {{ form.version ? 'v' + form.version : 'v1' }}
                        </span>
                        <span class="badge" :class="form.is_active ? 'bg-success-subtle text-success' : 'bg-danger-subtle text-danger'">
                            {{ form.is_active ? 'Ativo' : 'Inativo' }}
                        </span>
                    </div>
                    <span class="text-muted fs-7">{{ form.description || 'Configuração das instruções mestre da assistente virtual.' }}</span>
                </div>

                <div class="d-flex gap-2 align-items-center">
                    <button class="btn btn-pmg-light d-inline-flex align-items-center" @click="fetchPrompt" :disabled="saving" title="Recarregar prompt original">
                        <i class="bi bi-arrow-clockwise me-2" :class="{ 'spin-animation': loading }"></i> Recarregar
                    </button>
                    <button class="btn btn-pmg-primary d-inline-flex align-items-center" @click="savePrompt" :disabled="saving">
                        <span v-if="saving" class="spinner-border spinner-border-sm me-2" role="status"></span>
                        <i v-else class="bi bi-floppy me-2"></i>
                        {{ saving ? 'Salvando...' : 'Salvar Prompt' }}
                    </button>
                </div>
            </div>

            <div class="row g-4">
                <!-- Painel Esquerdo: Editor Principal com Destaque e Busca -->
                <div class="col-lg-8">
                     <div class="card card-body border-0 bg-light rounded-3 p-3">
                        <h6 class="fw-bold text-dark mb-3 fs-7">
                            <i class="bi bi-sliders me-1 text-primary"></i> Metadados do Agente
                        </h6>
                        <div class="row g-3">
                            <div class="col-md-8">
                                <label class="form-label small fw-bold text-muted">Título do Prompt</label>
                                <input type="text" v-model="form.title" class="form-control form-control-pmg bg-white" placeholder="Ex: Prompt Mestre - Suporte">
                            </div>
                            <div class="col-md-4">
                                <label class="form-label small fw-bold text-muted">Status do Agente</label>
                                <select v-model="form.is_active" class="form-select form-control-pmg bg-white">
                                    <option :value="true">Ativo (Em uso)</option>
                                    <option :value="false">Inativo (Desativado)</option>
                                </select>
                            </div>
                            <div class="col-12">
                                <label class="form-label small fw-bold text-muted">Descrição Interna</label>
                                <input type="text" v-model="form.description" class="form-control form-control-pmg bg-white" placeholder="Descrição do propósito deste prompt para a equipe.">
                            </div>
                        </div>
                    </div>
                    <div class="mb-4">
                        <div class="d-flex justify-content-between align-items-center mb-2">
                            <label class="form-label small fw-bold text-dark mb-0">
                                <i class="bi bi-code-square me-1 text-primary"></i> Instruções do Prompt (Template Mestre)
                            </label>

                            <div class="d-flex align-items-center gap-2">
                                <button 
                                    class="btn btn-sm" 
                                    :class="showSearch ? 'btn-primary' : 'btn-pmg-light'" 
                                    @click="toggleSearch"
                                    title="Localizar e Substituir (Ctrl+F)"
                                >
                                    <i class="bi bi-search me-1"></i> Pesquisar / Substituir
                                </button>
                                <span class="text-muted fs-7">{{ formatNumber(form.content.length) }} caracteres</span>
                            </div>
                        </div>

                        <!-- BARRA DE PESQUISA E SUBSTITUIÇÃO -->
                        <div v-if="showSearch" class="card card-body bg-light border border-primary-subtle rounded-3 p-3 mb-3 slide-in">
                            <div class="row g-2 align-items-center">
                                <!-- Campo de Busca -->
                                <div class="col-md-5">
                                    <div class="input-group input-group-sm">
                                        <span class="input-group-text bg-white border-end-0"><i class="bi bi-search text-muted"></i></span>
                                        <input 
                                            ref="searchInput"
                                            v-model="searchQuery" 
                                            type="text" 
                                            class="form-control form-control-pmg border-start-0" 
                                            placeholder="Localizar texto..."
                                            @input="performSearch(false)"
                                            @keydown.enter.prevent="nextMatch"
                                        >
                                    </div>
                                </div>

                                <!-- Campo de Substituição -->
                                <div class="col-md-4">
                                    <div class="input-group input-group-sm">
                                        <span class="input-group-text bg-white border-end-0"><i class="bi bi-pencil text-muted"></i></span>
                                        <input 
                                            v-model="replaceQuery" 
                                            type="text" 
                                            class="form-control form-control-pmg border-start-0" 
                                            placeholder="Substituir por..."
                                        >
                                    </div>
                                </div>

                                <!-- Botões de Ação de Busca -->
                                <div class="col-md-3 d-flex align-items-center gap-1 justify-content-end">
                                    <button class="btn btn-sm btn-white border" @click="prevMatch" :disabled="!matches.length" title="Ocorrência Anterior">
                                        <i class="bi bi-chevron-up"></i>
                                    </button>
                                    <button class="btn btn-sm btn-white border" @click="nextMatch" :disabled="!matches.length" title="Próxima Ocorrência">
                                        <i class="bi bi-chevron-down"></i>
                                    </button>
                                    <button class="btn btn-sm btn-outline-primary" @click="replaceCurrent" :disabled="!matches.length" title="Substituir atual">
                                        Trocar
                                    </button>
                                    <button class="btn btn-sm btn-outline-secondary" @click="replaceAll" :disabled="!matches.length" title="Substituir todos">
                                        Tudo
                                    </button>
                                    <button class="btn btn-sm btn-link text-muted p-0 ms-1" @click="toggleSearch" title="Fechar Busca">
                                        <i class="bi bi-x-lg fs-7"></i>
                                    </button>
                                </div>

                                <!-- Resumo e Alternância do Painel estilo Word -->
                                <div class="col-12 d-flex justify-content-between align-items-center pt-2 fs-8 text-muted border-top mt-2">
                                    <div>
                                        <template v-if="searchQuery">
                                            <strong v-if="matches.length" class="text-primary">
                                                {{ currentMatchIndex + 1 }} de {{ matches.length }} ocorrência(s) encontrada(s)
                                            </strong>
                                            <span v-else class="text-danger">Nenhuma ocorrência encontrada.</span>
                                        </template>
                                        <template v-else>
                                            Digite um termo para destacar todas as ocorrências no texto.
                                        </template>
                                    </div>
                                    <button 
                                        v-if="matches.length" 
                                        class="btn btn-link btn-sm p-0 text-decoration-none fs-8 text-purple fw-bold"
                                        @click="showMatchList = !showMatchList"
                                    >
                                        <i class="bi bi-list-task me-1"></i>
                                        {{ showMatchList ? 'Ocultar Painel de Ocorrências' : 'Ver Painel de Navegação' }}
                                    </button>
                                </div>

                                <!-- PAINEL DE OCORRÊNCIAS ESTILO NAVEGAÇÃO DO WORD -->
                                <div v-if="showMatchList && matches.length" class="col-12 mt-2">
                                    <div class="matches-panel bg-white border rounded-3 p-2">
                                        <div class="text-muted fs-8 fw-bold mb-2 pb-1 border-bottom d-flex justify-content-between">
                                            <span>LISTA DE OCORRÊNCIAS NO PROMPT</span>
                                            <span>Clique em uma ocorrência para ir até ela</span>
                                        </div>
                                        <div class="matches-scroll-container">
                                            <div 
                                                v-for="(match, idx) in matches" 
                                                :key="idx" 
                                                class="match-item p-2 rounded-2 border-bottom d-flex align-items-center justify-content-between cursor-pointer"
                                                :class="{ 'bg-purple-subtle border-purple': idx === currentMatchIndex }"
                                                @click="jumpToMatch(idx)"
                                            >
                                                <div class="text-truncate me-2">
                                                    <span class="badge bg-secondary-subtle text-secondary me-2">Linha {{ match.line }}</span>
                                                    <span class="fs-8 text-dark" v-html="match.snippet"></span>
                                                </div>
                                                <i class="bi bi-arrow-right-circle text-primary fs-7"></i>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <!-- CONTAINER DO EDITOR COM DESTAQUE EM CAMADAS (BACKDROP + TEXTAREA) -->
                        <div class="editor-wrapper position-relative">
                            <!-- Camada de Destaque (Backdrop) -->
                            <div 
                                ref="backdrop" 
                                class="editor-backdrop prompt-textarea" 
                                aria-hidden="true" 
                                v-html="highlightedContent"
                            ></div>

                            <!-- Textarea Nativa Editável -->
                            <textarea
                                ref="promptTextarea"
                                v-model="form.content"
                                class="form-control form-control-pmg prompt-textarea editor-textarea"
                                rows="16"
                                placeholder="Digite as instruções do prompt mestre aqui..."
                                @scroll="syncScroll"
                                @input="onContentInput"
                                @keydown="handleEditorKeyDown"
                            ></textarea>
                        </div>
                    </div>
                </div>
                <!-- Painel Direito: Placeholders e Dicas -->
                <div class="col-lg-4">
                    <div class="card card-body border-0 bg-light rounded-3 p-3 mb-3">
                        <h6 class="fw-bold text-dark mb-2 fs-7">
                            <i class="bi bi-lightning-charge me-1 text-warning"></i> Variáveis Dinâmicas (Placeholders)
                        </h6>
                        <p class="text-muted fs-7 mb-3">
                            Clique em uma variável abaixo para inseri-la diretamente na posição do cursor no editor:
                        </p>

                        <div class="d-flex flex-column gap-2">
                            <div 
                                v-for="(description, tag) in availablePlaceholders" 
                                :key="tag"
                                class="placeholder-card p-2 bg-white rounded-3 border cursor-pointer hover-shadow"
                                @click="insertPlaceholder(tag)"
                            >
                                <div class="d-flex align-items-center justify-content-between mb-1">
                                    <code class="text-purple fw-bold fs-7">{{ tag }}</code>
                                    <i class="bi bi-plus-circle text-primary fs-7"></i>
                                </div>
                                <div class="text-muted fs-7">{{ description }}</div>
                            </div>
                        </div>
                    </div>

                    <div class="card card-body bg-purple-subtle border-0 rounded-3 p-3 text-purple">
                        <div class="fw-bold mb-1 fs-7">
                            <i class="bi bi-info-circle me-1"></i> Dica de Navegação
                        </div>
                        <p class="mb-0 fs-7 opacity-90">
                            Ao buscar um termo, abra o <strong>Painel de Ocorrências</strong> para visualizar todos os trechos e saltar diretamente para qualquer linha do documento.
                        </p>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import api from '@/services/api';

export default {
    name: 'AiPromptSettings',
    inject: ['globalLoading'],
    data() {
        return {
            loading: true,
            saving: false,
            promptKey: 'client_support_main',
            form: {
                title: '',
                description: '',
                content: '',
                is_active: true,
                version: 1
            },
            availablePlaceholders: {
                '{$financialContext}': 'Dados Financeiros',
                '{$contextFormatted}': 'Base de conhecimento (RAG)',
                '{$historyFormatted}': 'Histórico recente de conversas',
                '{$question}': 'Pergunta atual do cliente'
            },

            // Motor de Busca, Destaques e Navegação Estilo Word
            showSearch: false,
            showMatchList: true,
            searchQuery: '',
            replaceQuery: '',
            matches: [],
            currentMatchIndex: -1
        };
    },
    computed: {
        /**
         * Gera o HTML da camada de fundo (backdrop) com as marcas de destaque <mark>
         */
        highlightedContent() {
            if (!this.form.content) return '';

            const escapedText = this.escapeHtml(this.form.content);

            if (!this.searchQuery.trim() || !this.showSearch) {
                // Adiciona uma quebra no final para alinhar a altura exata do scroll com o textarea
                return escapedText + '\n';
            }

            const query = this.escapeHtml(this.searchQuery.trim());
            const regex = new RegExp(`(${this.escapeRegex(query)})`, 'gi');

            let matchCounter = 0;
            const highlighted = escapedText.replace(regex, (match) => {
                const isActive = matchCounter === this.currentMatchIndex;
                matchCounter++;
                const activeClass = isActive ? 'active' : '';
                return `<mark class="search-mark ${activeClass}">${match}</mark>`;
            });

            return highlighted + '\n';
        }
    },
    mounted() {
        this.fetchPrompt();
    },
    methods: {
        formatNumber(value) {
            if (!value && value !== 0) return '0';
            return Number(value).toLocaleString('pt-BR');
        },
        async fetchPrompt() {
            this.loading = true;
            try {
                const response = await api.get(`/ai-prompts/${this.promptKey}`);
                const resData = response.data.data || response.data;
                
                this.form = {
                    title: resData.title || '',
                    description: resData.description || '',
                    content: resData.content || '',
                    is_active: resData.is_active ?? true,
                    version: resData.version || 1
                };

                const apiPlaceholders = response.data.available_placeholders || resData.available_placeholders;
                if (apiPlaceholders && Object.keys(apiPlaceholders).length > 0) {
                    this.availablePlaceholders = apiPlaceholders;
                }
            } catch (error) {
                this.$emit('alert', 'Falha ao carregar as instruções do prompt.', 'error');
            } finally {
                this.loading = false;
            }
        },

        async savePrompt() {
            if (!this.form.content.trim()) {
                this.$emit('alert', 'O conteúdo do prompt não pode ficar vazio.', 'warning');
                return;
            }

            this.saving = true;
            try {
                if (this.globalLoading) {
                    this.globalLoading.show('Atualizando Prompt Mestre...');
                }

                const payload = {
                    title: this.form.title,
                    description: this.form.description,
                    content: this.form.content,
                    is_active: this.form.is_active
                };

                const response = await api.put(`/ai-prompts/${this.promptKey}`, payload);
                const updated = response.data.data || response.data;
                
                this.form.version = updated.version;
                this.$emit('alert', 'Prompt mestre atualizado e cache renovado com sucesso!');
            } catch (error) {
                const msg = error.response?.data?.message || 'Erro ao salvar o prompt.';
                this.$emit('alert', msg, 'error');
            } finally {
                if (this.globalLoading) {
                    this.globalLoading.hide();
                }
                this.saving = false;
            }
        },

        insertPlaceholder(tag) {
            const textarea = this.$refs.promptTextarea;
            if (!textarea) {
                this.form.content += ` ${tag} `;
                return;
            }

            const startPos = textarea.selectionStart;
            const endPos = textarea.selectionEnd;
            const currentContent = this.form.content;

            this.form.content = currentContent.substring(0, startPos) + tag + currentContent.substring(endPos);

            this.$nextTick(() => {
                textarea.focus();
                textarea.setSelectionRange(startPos + tag.length, startPos + tag.length);
                this.syncScroll();
            });
        },

        // --- SINCRONIZAÇÃO DE SCROLL (BACKDROP X TEXTAREA) ---
        syncScroll() {
            const textarea = this.$refs.promptTextarea;
            const backdrop = this.$refs.backdrop;
            if (textarea && backdrop) {
                backdrop.scrollTop = textarea.scrollTop;
                backdrop.scrollLeft = textarea.scrollLeft;
            }
        },

        onContentInput() {
            this.syncScroll();
            if (this.showSearch && this.searchQuery) {
                this.performSearch(false);
            }
        },

        // --- MOTOR DE BUSCA E NAVEGAÇÃO ESTILO WORD ---

        toggleSearch() {
            this.showSearch = !this.showSearch;
            if (this.showSearch) {
                this.$nextTick(() => {
                    if (this.$refs.searchInput) {
                        this.$refs.searchInput.focus();
                        this.$refs.searchInput.select();
                    }
                });
            } else {
                this.searchQuery = '';
                this.matches = [];
                this.currentMatchIndex = -1;
            }
        },

        handleEditorKeyDown(e) {
            if ((e.ctrlKey || e.metaKey) && (e.key.toLowerCase() === 'f' || e.key.toLowerCase() === 'u' || e.key.toLowerCase() === 'h')) {
                e.preventDefault();
                this.toggleSearch();
            }
        },

        performSearch(shouldHighlight = false) {
            this.matches = [];
            this.currentMatchIndex = -1;

            if (!this.searchQuery.trim()) return;

            const content = this.form.content;
            const query = this.searchQuery.trim().toLowerCase();
            let index = content.toLowerCase().indexOf(query);

            while (index !== -1) {
                // Calcula a linha correspondente no documento
                const lineNumber = content.substring(0, index).split('\n').length;
                
                // Monta o trecho de contexto ao redor da palavra para o Painel do Word
                const snippetStart = Math.max(0, index - 25);
                const snippetEnd = Math.min(content.length, index + query.length + 25);
                const rawSnippet = content.substring(snippetStart, snippetEnd);
                
                const safeSnippet = this.escapeHtml(rawSnippet);
                const escapedQuery = this.escapeHtml(this.searchQuery.trim());
                const regexSnippet = new RegExp(`(${this.escapeRegex(escapedQuery)})`, 'gi');
                const formattedSnippet = safeSnippet.replace(regexSnippet, '<b class="text-purple bg-warning-subtle px-1 rounded">$1</b>');

                this.matches.push({
                    start: index,
                    end: index + query.length,
                    line: lineNumber,
                    snippet: (snippetStart > 0 ? '...' : '') + formattedSnippet + (snippetEnd < content.length ? '...' : '')
                });

                index = content.toLowerCase().indexOf(query, index + 1);
            }

            if (this.matches.length > 0) {
                this.currentMatchIndex = 0;
                if (shouldHighlight) {
                    this.highlightCurrentMatch();
                }
            }
        },

        jumpToMatch(index) {
            if (index >= 0 && index < this.matches.length) {
                this.currentMatchIndex = index;
                this.highlightCurrentMatch();
            }
        },

        nextMatch() {
            if (!this.searchQuery.trim()) return;
            if (!this.matches.length) {
                this.performSearch(true);
                return;
            }

            this.currentMatchIndex = (this.currentMatchIndex + 1) % this.matches.length;
            this.highlightCurrentMatch();
        },

        prevMatch() {
            if (!this.matches.length) return;
            this.currentMatchIndex = (this.currentMatchIndex - 1 + this.matches.length) % this.matches.length;
            this.highlightCurrentMatch();
        },

        highlightCurrentMatch() {
            if (this.currentMatchIndex < 0 || this.currentMatchIndex >= this.matches.length) return;

            const match = this.matches[this.currentMatchIndex];
            const textarea = this.$refs.promptTextarea;

            if (textarea) {
                textarea.focus();
                textarea.setSelectionRange(match.start, match.end);

                // Rolagem ajustada para centralizar a palavra encontrada na tela
                const lineHeight = 21;
                const targetScrollTop = (match.line - 4) * lineHeight;
                textarea.scrollTop = Math.max(0, targetScrollTop);
                this.syncScroll();
            }
        },

        replaceCurrent() {
            if (this.currentMatchIndex < 0 || !this.matches.length) return;

            const match = this.matches[this.currentMatchIndex];
            const content = this.form.content;

            this.form.content = content.substring(0, match.start) + this.replaceQuery + content.substring(match.end);

            this.$nextTick(() => {
                this.performSearch(true);
            });
        },

        replaceAll() {
            if (!this.searchQuery.trim() || !this.matches.length) return;

            const escapedQuery = this.escapeRegex(this.searchQuery.trim());
            const regex = new RegExp(escapedQuery, 'gi');

            this.form.content = this.form.content.replace(regex, this.replaceQuery);
            this.performSearch(false);
            this.$emit('alert', 'Todas as ocorrências foram substituídas com sucesso!');
        },

        // --- UTILITÁRIOS DE SEGURANÇA E REGEX ---
        escapeHtml(text) {
            return text
                .replace(/&/g, '&amp;')
                .replace(/</g, '&lt;')
                .replace(/>/g, '&gt;')
                .replace(/"/g, '&quot;')
                .replace(/'/g, '&#039;');
        },

        escapeRegex(string) {
            return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        }
    }
};
</script>

<style scoped>
/* BOTÕES PADRONIZADOS PMG */
.btn-pmg-primary {
    background: #561569;
    color: #fff;
    border: none;
    border-radius: 10px;
    padding: 10px 20px;
    font-weight: 500;
    transition: all 0.2s ease;
}

.btn-pmg-primary:hover:not(:disabled) {
    background: #19274a;
}

.btn-pmg-primary:disabled {
    opacity: 0.7;
    cursor: not-allowed;
}

.btn-pmg-light {
    background: #fff;
    border: 1px solid #e2e8f0;
    border-radius: 10px;
    color: #64748b;
    padding: 10px 20px;
    font-weight: 500;
    transition: all 0.2s ease;
}

.btn-pmg-light:hover:not(:disabled) {
    background: #f8fafc;
    color: #334155;
}

.btn-white {
    background-color: #ffffff;
    color: #475569;
}

.btn-white:hover {
    background-color: #f8fafc;
    color: #1e293b;
}

/* FORM CONTROLS PMG */
.form-control-pmg {
    border-radius: 10px;
    border: 1px solid #e2e8f0;
    padding: 12px;
    font-size: 0.95rem;
    color: #334155;
}

.form-control-pmg:focus {
    border-color: #561569;
    box-shadow: 0 0 0 4px rgba(33, 51, 97, 0.1);
    outline: none;
}

/* CONTAINER E EDITOR EM CAMADAS (BACKDROP + TEXTAREA) */
.editor-wrapper {
    position: relative;
    width: 100%;
}

.prompt-textarea {
    font-family: 'Fira Code', 'Cascadia Code', Consolas, Monaco, monospace;
    font-size: 0.88rem;
    line-height: 1.5;
    padding: 12px;
    tab-size: 4;
}

.editor-backdrop {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    pointer-events: none;
    color: transparent !important;
    background-color: #ffffff;
    overflow-y: auto;
    overflow-x: hidden;
    white-space: pre-wrap;
    word-wrap: break-word;
    word-break: break-word;
    border: 1px solid #e2e8f0;
    border-radius: 10px;
    z-index: 1;
}

.editor-textarea {
    position: relative;
    background: transparent !important;
    z-index: 2;
    caret-color: #1e293b;
}

/* DESTAQUE DE PALAVRAS NO TEXTO (HIGHLIGHT MARKS) */
:deep(.search-mark) {
    background-color: #fef08a !important; /* Amarelo translúcido para todas as ocorrências */
    color: transparent !important;
    border-radius: 3px;
    padding: 0 1px;
}

:deep(.search-mark.active) {
    background-color: #f97316 !important; /* Laranja em destaque para a ocorrência ativa */
    color: transparent !important;
    border-radius: 3px;
    box-shadow: 0 0 0 2px #c2410c;
}

/* PAINEL DE OCORRÊNCIAS ESTILO WORD */
.matches-panel {
    border-color: #cbd5e1 !important;
}

.matches-scroll-container {
    max-height: 160px;
    overflow-y: auto;
}

.match-item {
    transition: all 0.15s ease;
}

.match-item:hover {
    background-color: #fdf4ff !important;
}

.border-purple {
    border-color: #a855f7 !important;
}

/* UTILITÁRIOS DE ESTILO */
.placeholder-card {
    transition: all 0.2s ease;
}

.placeholder-card:hover {
    border-color: #561569 !important;
    background-color: #fdf4ff !important;
}

.cursor-pointer {
    cursor: pointer;
}

.bg-purple-subtle {
    background-color: #f3e8ff !important;
}

.text-purple {
    color: #7e22ce !important;
}

.bg-warning-subtle {
    background-color: #fef08a !important;
}

.bg-success-subtle {
    background-color: #ecfdf5 !important;
    color: #059669 !important;
}

.bg-danger-subtle {
    background-color: #fef2f2 !important;
    color: #dc2626 !important;
}

.bg-secondary-subtle {
    background-color: #f1f5f9 !important;
    color: #64748b !important;
}

.fs-7 {
    font-size: 0.85rem;
}

.fs-8 {
    font-size: 0.75rem;
}

.slide-in {
    animation: fadeSlide 0.3s ease-out;
}

@keyframes fadeSlide {
    from {
        opacity: 0;
        transform: translateY(5px);
    }
    to {
        opacity: 1;
        transform: translateY(0);
    }
}

.spin-animation {
    animation: spin 1s linear infinite;
}

@keyframes spin {
    100% {
        transform: rotate(360deg);
    }
}
</style>